import { Link, useLocation, type LinkProps } from 'react-router-dom';

/**
 * A link that opens its destination in the side drawer.
 *
 * ## What it actually does
 *
 * Pushes the same URL a plain `<Link>` would, and carries the page you are on
 * in `location.state.background`. `AppRoutes` renders the background as the
 * page and the URL as a drawer over it. Nothing about the address changes, so
 * copying it, sharing it or reloading it all behave the way they did before -
 * a cold load has no background and gets the full page.
 *
 * ## Opening a second one keeps the first background
 *
 * `background ?? location` rather than always `location`. Clicking a track
 * inside an album drawer would otherwise record the album's URL as the page
 * behind, and closing would land on a page nobody had been looking at.
 *
 * ## It always pushes, and that is not the default
 *
 * `<Link>` decides for itself whether to push or replace:
 *
 *     replace = replaceProp !== undefined ? replaceProp : createPath(location) === createPath(path)
 *
 * A link to the page you are already on replaces the current entry rather than
 * adding one. Sensible for a link, wrong for a drawer: `Drawer` closes with
 * `navigate(-1)`, so an open that replaced means a close pops the entry
 * *underneath* and lands somewhere nobody was looking.
 *
 * It took a peculiar shape to surface. You have to be on somebody's page and
 * click a link to that same person - which is every live room. The room is at
 * `/{host}`, the host does most of the talking, and their name beside each line
 * of chat points at the page you are standing on. Clicking it destroyed the
 * room's history entry and closing the drawer went back past the room to the
 * dashboard. Hosts never saw it: they run rooms from `/console`, which no
 * username can equal.
 *
 * `replace={false}` rather than a cleverer `close`. The alternative was to have
 * the drawer navigate to its background instead of popping, and that trades one
 * quiet failure for another - it would grow the history by an entry per drawer,
 * so the back button becomes a walk through everything anybody glanced at.
 * Pushing is what closing already assumes; this just stops the router quietly
 * deciding otherwise.
 *
 * ## Deliberately not automatic
 *
 * A `<Link to="/tracks/1">` still navigates. Whether something belongs in a
 * drawer is a judgement about that link - the dock's title bar opens one, an
 * "open full page" link must not - and a router-level rule that guessed would
 * be wrong somewhere unnoticeable.
 */
export function DrawerLink({ to, children, ...rest }: LinkProps) {
  return (
    // Before the spread, so a caller that genuinely wants a replace still wins.
    <Link to={to} replace={false} state={useDrawerState()} {...rest}>
      {children}
    </Link>
  );
}

/**
 * The state a link needs to open in the drawer, for callers that cannot be a
 * `DrawerLink`.
 *
 * `LinkButton` is the one that matters: "Edit profile" is a button, and
 * wrapping it in another anchor to get this behaviour would nest two links.
 * Exported rather than copied so there is still exactly one definition of what
 * "open in a drawer" means.
 */
export function useDrawerState(): { background: unknown } {
  const location = useLocation();
  const state = location.state as { background?: unknown } | null;

  // `background ?? location`, so opening a second drawer keeps the page the
  // first one was over rather than recording the first drawer's URL.
  return { background: state?.background ?? location };
}
